import React, { useEffect, useState } from 'react'
import { Loader2Icon, MailIcon, PlusIcon, KeyIcon, Trash2Icon } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { adminFetch, formatAdminApiError } from '../../lib/adminApi'

interface MailboxRow {
  id: string
  email_address: string
  display_name: string
  mailbox_type: 'shared' | 'personal'
  owner_user_id: string | null
  aliases: string[]
  is_active: boolean
  last_polled_at: string | null
  has_credentials: boolean
}

interface UserOption {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
}

const emptyForm = {
  email_address: '',
  display_name: '',
  mailbox_type: 'personal' as 'shared' | 'personal',
  owner_user_id: '',
  app_password: '',
  aliases: '',
}

/**
 * Settings → Mailboxes (communication.inbox_admin). Connects Gmail/Workspace
 * accounts to the Inbox. App passwords are sent once to the server, verified
 * with a live login, and stored server-side only — they are never readable
 * from the browser afterwards.
 */
export function MailboxSettings() {
  const [mailboxes, setMailboxes] = useState<MailboxRow[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState({ ...emptyForm })
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    loadMailboxes()
    loadUsers()
  }, [])

  async function loadMailboxes() {
    setLoading(true)
    try {
      const data = await adminFetch('/api/email/mailboxes', null)
      setMailboxes(data.mailboxes || [])
    } catch (err: any) {
      setMessage({ type: 'error', text: formatAdminApiError(err.message || 'Failed to load mailboxes') })
    } finally {
      setLoading(false)
    }
  }

  async function loadUsers() {
    const { data } = await supabase
      .from('user_profiles')
      .select('id, email, first_name, last_name')
      .eq('is_active', true)
      .order('first_name')
    setUsers(data || [])
  }

  function userLabel(userId: string | null) {
    const user = users.find((u) => u.id === userId)
    if (!user) return ''
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim()
    return name || user.email
  }

  async function handleAdd() {
    setSaving(true)
    setMessage(null)
    try {
      await adminFetch('/api/email/mailboxes', null, {
        method: 'POST',
        body: JSON.stringify({
          email_address: form.email_address.trim(),
          display_name: form.display_name.trim(),
          mailbox_type: form.mailbox_type,
          owner_user_id: form.mailbox_type === 'personal' ? form.owner_user_id || null : null,
          app_password: form.app_password,
          aliases:
            form.mailbox_type === 'shared'
              ? form.aliases.split(',').map((a) => a.trim()).filter(Boolean)
              : [],
        }),
      })
      setMessage({ type: 'success', text: `${form.email_address.trim()} connected. New mail will be imported from now on.` })
      setForm({ ...emptyForm })
      setFormOpen(false)
      await loadMailboxes()
    } catch (err: any) {
      setMessage({ type: 'error', text: formatAdminApiError(err.message || 'Failed to add mailbox') })
    } finally {
      setSaving(false)
    }
  }

  async function handleToggleActive(box: MailboxRow) {
    setBusyId(box.id)
    setMessage(null)
    try {
      await adminFetch(`/api/email/mailboxes/${box.id}`, null, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !box.is_active }),
      })
      await loadMailboxes()
    } catch (err: any) {
      setMessage({ type: 'error', text: formatAdminApiError(err.message || 'Failed to update mailbox') })
    } finally {
      setBusyId(null)
    }
  }

  async function handleUpdatePassword(box: MailboxRow) {
    const appPassword = window.prompt(`New Gmail app password for ${box.email_address}:`)
    if (!appPassword) return
    setBusyId(box.id)
    setMessage(null)
    try {
      await adminFetch(`/api/email/mailboxes/${box.id}`, null, {
        method: 'PATCH',
        body: JSON.stringify({ app_password: appPassword }),
      })
      setMessage({ type: 'success', text: `App password updated for ${box.email_address}.` })
      await loadMailboxes()
    } catch (err: any) {
      setMessage({ type: 'error', text: formatAdminApiError(err.message || 'Failed to update password') })
    } finally {
      setBusyId(null)
    }
  }

  async function handleDelete(box: MailboxRow) {
    if (!window.confirm(`Remove ${box.email_address}? This only disconnects it from the app.`)) return
    setBusyId(box.id)
    setMessage(null)
    try {
      await adminFetch(`/api/email/mailboxes/${box.id}`, null, { method: 'DELETE' })
      await loadMailboxes()
    } catch (err: any) {
      setMessage({ type: 'error', text: formatAdminApiError(err.message || 'Failed to remove mailbox') })
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Email Mailboxes</h2>
          <p className="mt-1 text-sm text-gray-600">
            Accounts the Inbox sends and receives email from. Personal mailboxes are only visible to
            their owner (and inbox admins). Passwords are Gmail app passwords
            (Google Account → Security → App passwords) and are stored server-side only.
          </p>
        </div>
        <button
          onClick={() => setFormOpen((v) => !v)}
          className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Mailbox
        </button>
      </div>

      {message && (
        <div
          className={`mt-4 rounded-lg p-3 text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {formOpen && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                value={form.email_address}
                onChange={(e) => setForm({ ...form, email_address: e.target.value })}
                placeholder="abe@rbcfs.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
              <input
                type="text"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="Abe"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={form.mailbox_type}
                onChange={(e) => setForm({ ...form, mailbox_type: e.target.value as 'shared' | 'personal' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="personal">Personal — only the owner sees its mail</option>
                <option value="shared">Shared — everyone with Inbox access sees it</option>
              </select>
            </div>
            {form.mailbox_type === 'personal' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Owner</label>
                <select
                  value={form.owner_user_id}
                  onChange={(e) => setForm({ ...form, owner_user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select user…</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aliases <span className="text-gray-400">(optional, comma separated)</span>
                </label>
                <input
                  type="text"
                  value={form.aliases}
                  onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                  placeholder="info@rbcfs.com, office@rbcfs.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Gmail App Password</label>
              <input
                type="password"
                value={form.app_password}
                onChange={(e) => setForm({ ...form, app_password: e.target.value })}
                placeholder="16-character app password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                The login is tested before saving. Only mail received after the mailbox is added is
                imported — the existing history stays in Gmail.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setFormOpen(false)
                setForm({ ...emptyForm })
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={
                saving ||
                !form.email_address.trim() ||
                !form.app_password.trim() ||
                (form.mailbox_type === 'personal' && !form.owner_user_id)
              }
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <PlusIcon className="w-4 h-4" />}
              {saving ? 'Verifying login…' : 'Connect Mailbox'}
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {mailboxes.length === 0 ? (
          <p className="text-sm text-gray-500">No mailboxes connected yet.</p>
        ) : (
          mailboxes.map((box) => (
            <div key={box.id} className="rounded-lg border border-gray-200 p-4 flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                <MailIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">{box.email_address}</span>
                  <span
                    className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                      box.mailbox_type === 'shared' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}
                  >
                    {box.mailbox_type === 'shared' ? 'Shared' : `Personal — ${userLabel(box.owner_user_id) || 'unknown'}`}
                  </span>
                  {!box.is_active && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      Inactive
                    </span>
                  )}
                  {!box.has_credentials && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                      No password saved
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {box.display_name && <>{box.display_name} &middot; </>}
                  {box.aliases?.length > 0 && <>Aliases: {box.aliases.join(', ')} &middot; </>}
                  {box.last_polled_at
                    ? `Last checked ${new Date(box.last_polled_at).toLocaleString()}`
                    : 'Not checked yet'}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handleUpdatePassword(box)}
                  disabled={busyId === box.id}
                  title="Update app password"
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                >
                  <KeyIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleToggleActive(box)}
                  disabled={busyId === box.id}
                  className="px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {box.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(box)}
                  disabled={busyId === box.id}
                  title="Remove mailbox"
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                >
                  <Trash2Icon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
